import React, { useRef, useEffect, useState } from "react";
import { motion } from "motion/react";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { pillars, type Pillar } from "../data/pillars";
gsap.registerPlugin(ScrollTrigger);

/* ─── NOISE TEXTURE (inline SVG data-URI) ─── */
const NOISE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.08'/%3E%3C/svg%3E")`;

/* ─── PILLAR CARD ───────────────────────────── */
function PillarCard({ pillar, index }: { pillar: Pillar; index: number }) {
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const orbRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [hovered, setHovered] = useState(false);
  const isEven = index % 2 === 1;

  /* ── Scroll-triggered card entrance ── */
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: cardRef.current,
          start: "top 85%",
          end: "top 40%",
          toggleActions: "play none none reverse",
          lazy: true,
        } as any,
      });

      // Card slides in from the correct side
      tl.fromTo(
        cardRef.current,
        { autoAlpha: 0, x: isEven ? 80 : -80, rotationY: isEven ? 4 : -4 },
        { autoAlpha: 1, x: 0, rotationY: 0, duration: 1.1, ease: "expo.out" },
      )
        .fromTo(
          iconRef.current,
          { scale: 0.6, autoAlpha: 0, rotation: -15 },
          {
            scale: 1,
            autoAlpha: 1,
            rotation: 0,
            duration: 0.85,
            ease: "back.out(1.7)",
          },
          "-=0.6",
        )
        .fromTo(
          lineRef.current,
          { scaleX: 0, transformOrigin: "left center" },
          { scaleX: 1, duration: 0.6, ease: "power2.out" },
          "-=0.4",
        )
        .fromTo(
          textRef.current,
          { autoAlpha: 0, y: 20 },
          { autoAlpha: 1, y: 0, duration: 0.7, ease: "power3.out" },
          "-=0.4",
        )
        .fromTo(
          btnRef.current,
          { autoAlpha: 0, y: 12 },
          { autoAlpha: 1, y: 0, duration: 0.5, ease: "power2.out" },
          "-=0.25",
        );
    });
    return () => ctx.revert();
  }, [isEven]);

  /* ── Icon idle float ── */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(iconRef.current, {
        y: -8,
        duration: 2.4,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
        delay: index * 0.4,
      });
    });
    return () => ctx.revert();
  }, [index]);

  /* ── Orb parallax on mouse move ── */
  const handleMouseMove = (e: React.MouseEvent) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const mx = (e.clientX - rect.left - rect.width / 2) / rect.width;
    const my = (e.clientY - rect.top - rect.height / 2) / rect.height;
    gsap.to(orbRef.current, {
      x: mx * 40,
      y: my * 40,
      duration: 0.6,
      ease: "power2.out",
    });
  };

  const handleMouseLeave = () => {
    gsap.to(orbRef.current, { x: 0, y: 0, duration: 0.9, ease: "expo.out" });
  };

  /* ── Button magnetic effect ── */
  const handleBtnMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const el = btnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const dx = e.clientX - (rect.left + rect.width / 2);
    const dy = e.clientY - (rect.top + rect.height / 2);
    gsap.to(btnRef.current, {
      x: dx * 0.35,
      y: dy * 0.35,
      duration: 0.35,
      ease: "power2.out",
    });
  };

  const handleBtnLeave = () => {
    gsap.to(btnRef.current, {
      x: 0,
      y: 0,
      duration: 0.6,
      ease: "elastic.out(1, 0.5)",
    });
  };

  return (
    <div
      ref={cardRef}
      onClick={() => navigate(pillar.link)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={() => setHovered(true)}
      onMouseOut={() => setHovered(false)}
      className="p-6 sm:p-8 md:p-12 rounded-2xl md:rounded-[2rem] gap-6 md:gap-12 cursor-pointer active:scale-[0.99] md:active:scale-100 transition-all duration-300"
      style={{
        opacity: 0,
        position: "relative",
        display: "flex",
        flexDirection: isEven ? "row-reverse" : "row",
        flexWrap: "wrap",
        border: `1px solid rgba(${pillar.accentRgb}, 0.15)`,
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)",
        backdropFilter: "blur(24px)",
        overflow: "hidden",
        transition: "border-color 0.4s ease",
        perspective: "1200px",
      }}
    >
      {/* ── Noise overlay ── */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: NOISE,
          backgroundSize: "200px 200px",
          pointerEvents: "none",
          zIndex: 1,
          opacity: 0.6,
        }}
      />

      {/* ── Animated orb ── */}
      <div
        ref={orbRef}
        style={{
          position: "absolute",
          width: "420px",
          height: "420px",
          borderRadius: "50%",
          background: `radial-gradient(circle, rgba(${pillar.accentRgb}, 0.18) 0%, transparent 70%)`,
          top: isEven ? "auto" : "-120px",
          bottom: isEven ? "-120px" : "auto",
          left: isEven ? "auto" : "-120px",
          right: isEven ? "-120px" : "auto",
          filter: "blur(40px)",
          pointerEvents: "none",
          zIndex: 0,
          transition: "opacity 0.4s ease",
          opacity: hovered ? 1 : 0.4,
        }}
      />

      {/* Icon Panel - full height, half width */}
      <div
        ref={iconRef}
        className="w-full md:w-auto h-44 sm:h-56 md:min-h-[320px] rounded-xl md:rounded-[1.5rem]"
        style={{
          position: "relative",
          zIndex: 2,
          flex: "1 1 0%",
          minWidth: "260px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, rgba(${pillar.accentRgb}, 0.12), rgba(${pillar.accentRgb}, 0.04))`,
          border: `1px solid rgba(${pillar.accentRgb}, 0.2)`,
          boxShadow: `0 0 60px rgba(${pillar.accentRgb}, 0.15) inset, 0 20px 60px rgba(0,0,0,0.4)`,
        }}
      >
        <pillar.icon
          className="w-14 h-14 md:w-20 md:h-20"
          size={80}
          style={{
            color: pillar.accent,
            filter: `drop-shadow(0 0 20px ${pillar.accent}88)`,
          }}
        />
      </div>

      {/* Text */}
      <div
        ref={textRef}
        style={{
          position: "relative",
          zIndex: 2,
          flex: "1 1 0%",
          minWidth: "260px",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          justifyContent: "center",
        }}
      >
        {/* Tag pill */}
        <span
          style={{
            display: "inline-flex",
            alignSelf: "flex-start",
            padding: "0.25rem 0.85rem",
            borderRadius: "9999px",
            fontSize: "0.65rem",
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 700,
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            color: "#fff",
            background: "rgba(255,255,255,0.08)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.15)",
          }}
        >
          {pillar.tag}
        </span>

        <h3
          style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: "clamp(1.75rem, 4vw, 3.8rem)",
            fontWeight: 700,
            letterSpacing: "0.04em",
            lineHeight: 1.05,
            color: "#f8f8f8",
            margin: 0,
          }}
        >
          {pillar.title}
        </h3>

        {/* Accent line */}
        <div
          ref={lineRef}
          style={{
            height: "2px",
            width: "100%",
            maxWidth: "280px",
            background: `linear-gradient(90deg, ${pillar.accent}, transparent)`,
            borderRadius: "2px",
          }}
        />

        <p
          style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: "0.95rem",
            lineHeight: 1.75,
            color: "rgba(255,255,255,0.45)",
            margin: 0,
            textAlign: "justify",
          }}
        >
          {pillar.description}
        </p>

        {/* CTA Button */}
        <div style={{ marginTop: "0.75rem" }}>
          <button
            ref={btnRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(pillar.link);
            }}
            onMouseMove={handleBtnMove}
            onMouseLeave={handleBtnLeave}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.65rem 1.5rem",
              borderRadius: "9999px",
              color: "#fff",
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 700,
              fontSize: "0.7rem",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              cursor: "pointer",
              transition: "background 0.3s, box-shadow 0.3s",
              background: "rgba(255,255,255,0.08)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.15)",
              boxShadow: hovered ? `0 0 20px rgba(255,255,255,0.15)` : "none",
            }}
            onMouseEnter={(e) => {
              gsap.to(e.currentTarget.querySelector(".arrow-icon"), {
                x: 5,
                duration: 0.3,
                ease: "power2.out",
              });
            }}
            onMouseOut={(e) => {
              gsap.to(e.currentTarget.querySelector(".arrow-icon"), {
                x: 0,
                duration: 0.5,
                ease: "elastic.out(1, 0.5)",
              });
            }}
          >
            Explore
            <ArrowRight size={13} className="arrow-icon" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── SECTION HEADER ───────────────────────── */
function SectionHeader() {
  const headRef = useRef(null);
  const lineRef = useRef(null);
  const subRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: headRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse",
          lazy: true,
        } as any,
      });
      tl.fromTo(
        lineRef.current,
        { scaleX: 0, transformOrigin: "left" },
        { scaleX: 1, duration: 0.6, ease: "power2.out" },
      )
        .fromTo(
          subRef.current,
          { autoAlpha: 0, x: -20 },
          { autoAlpha: 1, x: 0, duration: 0.5, ease: "power2.out" },
          "-=0.3",
        )
        .fromTo(
          titleRef.current,
          { autoAlpha: 0, y: 30 },
          { autoAlpha: 1, y: 0, duration: 0.8, ease: "expo.out" },
          "-=0.2",
        );
    });
    return () => ctx.revert();
  }, []);

  return (
    <div ref={headRef} className="mb-10 md:mb-20">
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="font-Montserrat text-xs tracking-widest uppercase text-indigo-600 dark:text-indigo-300/70 mb-6 flex items-center gap-4"
      >
        <div ref={lineRef} className="w-4 h-px bg-indigo-500/50" />
        <span ref={subRef}>Our Services</span>
      </motion.div>

      <h2
        ref={titleRef}
        className="font-Montserrat text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-black dark:text-white"
      >
        Our Services
      </h2>
    </div>
  );
}

/* ─── ROOT SECTION ─────────────────────────── */
export default function Capabilities() {
  return (
    <section className="relative py-14 md:py-20 lg:py-32 overflow-hidden bg-transparent z-10">
      {/* Ambient elements */}

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 relative z-2">
        <SectionHeader />

        <div className="flex flex-col gap-6 sm:gap-8 md:gap-12">
          {pillars.map((pillar, index) => (
            <PillarCard key={pillar.id} pillar={pillar} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
